import * as api from "./middleware/api.middleware";
import type { IDataVaultRelatedEntity } from "../interfaces/dataVaultModelling.interfaces";
import { getAuthHeader } from "./auth/authHeaders";
import type { GenerationReport } from "../components/modelling/GenerationReportSummary";
import { typedRequest } from "./middleware/request";

const getAccessToken = () => getAuthHeader();

const buildApiPath = (path: string) => (path.startsWith("/") ? path.slice(1) : path);
const DATA_VAULT_BASE_URL = (process.env.REACT_APP_API_URL ?? "").replace(/\/+$/, "");
const buildApiUrl = (path: string) => `${DATA_VAULT_BASE_URL}/${buildApiPath(path)}`;

const getEnvironmentId = () => localStorage.getItem("environment_id");
const getCustomerId = () => localStorage.getItem("customer_id");

export const SOURCE_ENTITY_COLUMNS_DEFAULT_PAGE_SIZE = 10;

export function getDataVaultRelatedEntities(
  environmentId?: string | number | null,
  customerId?: string | number | null
) {
  const resolvedEnvironmentId = environmentId ?? getEnvironmentId();
  const resolvedCustomerId = customerId ?? getCustomerId();

  if (!resolvedEnvironmentId || !resolvedCustomerId) {
    return Promise.reject(
      new Error("Missing environment or customer context for Data Vault request.")
    );
  }

  const searchParams = new URLSearchParams({
    environment_id: String(resolvedEnvironmentId),
    customer_id: String(resolvedCustomerId)
  });

  const url = buildApiUrl(`/fetch-related-entities?${searchParams.toString()}`);
  return api.get<IDataVaultRelatedEntity[]>(url, getAccessToken(), {
    options: { isUrlExclusive: true }
  });
}

export type RawDataVaultSqlPreviewResponse = {
  target_path: string | null;
  sql: string;
  model_generation_id: string;
  generated_stored_procedures: Record<string, unknown>;
  is_onprem: boolean;
  /** Per-entity coverage of the run — which tables were generated, skipped, failed. */
  generation_report?: GenerationReport | null;
};

export function generateRawDataVaultSqlPreview(
  environmentId: string | number,
  subjectArea: string,
  fabricLakehouseName?: string | null,
  options?: { shouldGenerateInserts?: boolean; fileSystem?: string | null }
) {
  const trimmedSubjectArea = subjectArea.trim();
  if (!trimmedSubjectArea) {
    return Promise.reject(new Error("Select a subject area before generating SQL preview."));
  }

  const headers: Record<string, string> = {
    Accept: "application/json",
    "Subject-Area": trimmedSubjectArea,
    "Should-Generate-Inserts": options?.shouldGenerateInserts ? "true" : "false",
    // Preview only: report a failing entity in generation_report and keep generating
    // the rest. Deploy/load callers omit this and still fail loudly.
    "Allow-Partial-Generation": "true"
  };

  const trimmedLakehouse = fabricLakehouseName?.trim();
  if (trimmedLakehouse) {
    headers["Fabric-Lakehouse-Name"] = trimmedLakehouse;
  }

  // Storage container for LAKEHOUSE targets; feeds LOCATION '/mnt/{file_system}/...'.
  const trimmedFileSystem = options?.fileSystem?.trim();
  if (trimmedFileSystem) {
    headers["File-System"] = trimmedFileSystem;
  }

  return typedRequest<RawDataVaultSqlPreviewResponse>(
    buildApiUrl(`/environments/${environmentId}/generate_raw_data_vault`),
    {
      method: "POST",
      headers
    },
    {
      options: { isUrlExclusive: true }
    }
  );
}

export type DqTestSqlPreviewResponse = {
  status?: string;
  sql: string;
};

/**
 * Generate the Data Quality test SQL preview for an RDV subject area. Mirrors
 * generateRawDataVaultSqlPreview but hits the DQ endpoint, which takes the subject area in the
 * JSON body (not a header) and returns { status, sql }.
 */
export function generateDqTestRawDataVaultSqlPreview(
  environmentId: string | number,
  subjectArea: string
) {
  const trimmedSubjectArea = subjectArea.trim();
  if (!trimmedSubjectArea) {
    return Promise.reject(new Error("Select a subject area before generating DQ test preview."));
  }
  return api.post<DqTestSqlPreviewResponse>(
    buildApiUrl(`/environments/${environmentId}/generate_dq_test_raw_data_vault`),
    { subject_area: trimmedSubjectArea },
    getAccessToken(),
    { options: { isUrlExclusive: true } }
  );
}

export type CreateMappingPayload = {
  data_source_name: string;
  source_table: { value: string; id?: number | null };
  subject_area: string;
  ensemble: string;
  data_vault_construct_type: string;
  skip_metadata_tests: boolean;
  include_dq_test?: boolean;
  generate_model_entity?: boolean;
  is_custom_view_or_table?: boolean;
  /** LINKS only: build a row only where every business key has a value. */
  exclude_null_business_keys?: boolean;
  target_schema: string;
  target_table: { value: string; id?: number | null };
  source_filter?: string;
  dq_source_filter?: string;
  // Synapse dedicated SQL pool only; the backend normalizes and validates the pair
  // (HASH requires a key, every other type stores none).
  distribution_type?: string | null;
  distribution_key?: string | null;
  linked_hubs?: Array<{
    hub_target_entity_rdv_id?: number;
    hub_target_entity_rdv_name?: string;
    hub_role?: string | null;
  }>;
  parent_hub?: {
    hub_target_entity_rdv_id?: number;
    hub_target_entity_rdv_name?: string;
  };
  canvas_x?: number | null;
  canvas_y?: number | null;
};

export type CreateMappingResponseItem = {
  message: string;
  mapping_id: number;
  source_entity_id: number;
  target_entity_rdv_id: number;
};

export type CreateMappingResponse = {
  count: number;
  results: CreateMappingResponseItem[];
};

export type WorkspaceLayoutEntry = {
  entity_type: "hub" | "satellite" | "link" | "manual";
  target_table: string;
  target_entity_rdv_id?: number | null;
  canvas_x: number;
  canvas_y: number;
};

export function createDataVaultMappings(
  payloads: CreateMappingPayload[],
  environmentId?: string | number | null,
  customerId?: string | number | null
) {
  const resolvedEnvironmentId = environmentId ?? getEnvironmentId();
  const resolvedCustomerId = customerId ?? getCustomerId();

  if (!resolvedEnvironmentId || !resolvedCustomerId) {
    return Promise.reject(
      new Error("Missing environment or customer context for Data Vault write request.")
    );
  }

  const searchParams = new URLSearchParams({
    environment_id: String(resolvedEnvironmentId),
    customer_id: String(resolvedCustomerId)
  });

  const url = buildApiUrl(
    `/source-target-entity-rdv-mapping-create?${searchParams.toString()}`
  );

  return api.post<CreateMappingResponse>(url, payloads, getAccessToken(), {
    options: { isUrlExclusive: true }
  });
}

// Promote a custom (INTERNAL_REGISTERED) silver source entity into the Raw Data Vault. Mirrors the
// DM promote flow. v1 supports Hub (table_construct="HubAsTable"); Satellite is a planned fast-follow.
export function promoteInternalSilverAssetToRdv(
  payload: {
    source_entity_id: number;
    table_construct?: string;
    parent_hub_target_entity_rdv_id?: number | null;
    subject_area?: string | null;
    ensemble?: string | null;
    target_entity_rdv_name?: string | null;
    target_schema?: string | null;
    description?: string | null;
    include_dq_test?: boolean | null;
    generate_model_entity?: boolean | null;
    is_active?: boolean | null;
  },
  environmentId?: string | number | null,
  customerId?: string | number | null
) {
  const resolvedEnvironmentId = environmentId ?? getEnvironmentId();
  const resolvedCustomerId = customerId ?? getCustomerId();
  if (!resolvedEnvironmentId || !resolvedCustomerId) {
    return Promise.reject(
      new Error("Missing environment or customer context for promoting internal silver asset.")
    );
  }
  const searchParams = new URLSearchParams({
    environment_id: String(resolvedEnvironmentId),
    customer_id: String(resolvedCustomerId)
  });
  const url = buildApiUrl(
    `/target-entity-rdv-promote-internal-asset?${searchParams.toString()}`
  );
  return api.post<any>(url, payload, getAccessToken(), {
    options: { isUrlExclusive: true }
  });
}

export interface SourceEntityColumnMapping {
  source_target_entity_rdv_column_mapping_id: number | null;
  source_entity_column_id: number | null;
  source_entity_column_name: string | null;
  source_entity_id: number | null;
  source_entity_name: string | null;
  target_entity_rdv_id: number | null;
  target_entity_rdv_name: string | null;
  target_schema: string | null;
  target_entity_rdv_column_id: number | null;
  target_entity_rdv_column_name: string | null;
  business_key: string | boolean | null;
  business_key_order?: number | null;
  business_key_for_target_entity_rdv_id: number | null;
  business_key_for?: number | null;
  business_key_for_target_entity_rdv_name?: string | null;
  hash_key: string | boolean | null;
  pii: string | boolean | null;
  description: string | null;
  target_entity_rdv_column_transform: string | null;
  is_inherited?: boolean | null;
  inherited_from_target_entity_rdv_id?: number | null;
  inherited_from_target_entity_rdv_name?: string | null;
  is_locked?: boolean | null;
  /**
   * A business key inherited from the hub, seeded for this entity to map: the target
   * column name and order are the hub's (that is what keeps the hashes equal), the source
   * column and transform are this entity's own.
   */
  is_canonical_key?: boolean | null;
  /** True when this entity already carries its own column for the inherited key. */
  already_mapped?: boolean | null;
  /** True when the column has no source column and its transform holds a literal value. */
  is_constant?: boolean | null;
  /**
   * Which hub COLUMN this business key realises. The identity of "the same key" across a
   * hub and its satellites — the target name cannot serve, because a satellite reading a
   * different source table may legitimately call its copy something else.
   */
  business_key_for_target_entity_rdv_column_id?: number | null;
  /**
   * Stable client-side row identity for a row that has no ids yet. A new constant column
   * has neither a source column id nor a target column id, so without this every one of
   * them keys the same and edits land on the wrong row.
   */
  client_row_key?: string | null;
  /**
   * The target column name exactly as the API returned it — null when this source column
   * is not modelled yet. The modal defaults the displayed target name to the source column
   * name, which would otherwise make an unmapped column look modelled.
   */
  mapped_target_entity_rdv_column_name?: string | null;
}

export interface SourceEntityColumnsResponse {
  items: SourceEntityColumnMapping[];
  parent_hub?: {
    hub_target_entity_rdv_id: number | null;
    hub_target_entity_rdv_name: string | null;
  } | null;
  linked_hubs?: Array<{
    hub_target_entity_rdv_id: number | null;
    hub_target_entity_rdv_name: string | null;
    hub_role?: string | null;
  }>;
  inherited_business_key_items?: SourceEntityColumnMapping[];
  inherited_link_business_key_items?: SourceEntityColumnMapping[];
  page?: number;
  page_size?: number;
  total_count?: number;
  total_pages?: number;
}

export interface SourceEntityColumnAutocompleteResponse {
  source_entity_column_names: string[];
  target_entity_rdv_column_names: string[];
}

export function fetchSourceEntityColumns(params: {
  source_entity_id: number;
  environment_id: number;
  customer_id: number;
  target_entity_rdv_id?: number;
  for_modelling?: boolean;
}) {
  const searchParams = new URLSearchParams({
    source_entity_id: String(params.source_entity_id),
    environment_id: String(params.environment_id),
    customer_id: String(params.customer_id)
  });

  if (typeof params.for_modelling === "boolean") {
    searchParams.set("for_modelling", String(params.for_modelling));
  }

  if (params.target_entity_rdv_id) {
    searchParams.set("target_entity_rdv_id", String(params.target_entity_rdv_id));
  }
  const url = buildApiUrl(`/fetch-source-entity-columns?${searchParams.toString()}`);
  return api.get<SourceEntityColumnsResponse | SourceEntityColumnMapping[]>(url, getAccessToken(), {
    options: { isUrlExclusive: true }
  });
}

export function fetchSourceEntityColumnAutocomplete(params: {
  source_entity_id: number;
  environment_id: number;
  customer_id?: number;
  target_entity_rdv_id?: number;
}) {
  const searchParams = new URLSearchParams({
    source_entity_id: String(params.source_entity_id),
    environment_id: String(params.environment_id)
  });

  if (typeof params.customer_id === "number") {
    searchParams.set("customer_id", String(params.customer_id));
  }

  if (typeof params.target_entity_rdv_id === "number") {
    searchParams.set("target_entity_rdv_id", String(params.target_entity_rdv_id));
  }

  const url = buildApiUrl(`/fetch-source-entity-column-autocomplete?${searchParams.toString()}`);
  return api.get<SourceEntityColumnAutocompleteResponse>(url, getAccessToken(), {
    options: { isUrlExclusive: true }
  });
}

export type SaveColumnMappingPayload = {
  source_target_entity_rdv_column_mapping_id?: number | null;
  source_entity_column_id?: number | null;
  source_entity_id?: number | null;
  source_entity_column_name?: string | null;
  target_entity_rdv_id: number | null;
  target_entity_rdv_column_id?: number | null;
  target_entity_rdv_column_name: string | null;
  business_key: boolean;
  business_key_order: number | null;
  business_key_for: number | null;
  hash_key: boolean;
  pii: boolean;
  description: string | null;
  target_entity_rdv_column_transform?: string | null;
  /**
   * A constant column: no source column, the transform holds the value. SGDS's BKCC is the
   * canonical case — the common views stamp the business they serve ('DAHL', 'OPT'), which
   * nothing in the source produces.
   */
  is_constant?: boolean;
  /** The hub column this business key realises; see SourceEntityColumnMapping. */
  business_key_for_target_entity_rdv_column_id?: number | null;
};

export type SaveColumnMappingsResponse =
  | SourceEntityColumnMapping[]
  | {
    results?: SourceEntityColumnMapping[];
    message?: string;
  };

export type DeleteColumnMappingPayload = {
  source_target_entity_rdv_column_mapping_id?: number | null;
  target_entity_rdv_column_id?: number | null;
};

export type DeleteTargetEntityResponse = {
  status: string;
  deleted_type: "HUB" | "SATELLITE" | "LINK";
  deleted_hub_id?: number;
  deleted_satellite_id?: number;
  deleted_link_id?: number;
  cascade?: string;
};

export function createSourceEntityColumnMappings(
  payloads: SaveColumnMappingPayload[],
  environmentId?: string | number | null,
  customerId?: string | number | null
) {
  const resolvedEnvironmentId = environmentId ?? getEnvironmentId();
  const resolvedCustomerId = customerId ?? getCustomerId();

  if (!resolvedEnvironmentId || !resolvedCustomerId) {
    return Promise.reject(
      new Error("Missing environment or customer context for Data Vault write request.")
    );
  }

  const searchParams = new URLSearchParams({
    environment_id: String(resolvedEnvironmentId),
    customer_id: String(resolvedCustomerId)
  });

  const url = buildApiUrl(
    `/source-target-entity-rdv-column-mapping-create?${searchParams.toString()}`
  );
  return api.post<SaveColumnMappingsResponse>(url, payloads, getAccessToken(), {
    options: { isUrlExclusive: true }
  });
}

export function updateSourceEntityColumnMappings(
  payloads: SaveColumnMappingPayload[],
  environmentId?: string | number | null,
  customerId?: string | number | null
) {
  const resolvedEnvironmentId = environmentId ?? getEnvironmentId();
  const resolvedCustomerId = customerId ?? getCustomerId();

  if (!resolvedEnvironmentId || !resolvedCustomerId) {
    return Promise.reject(
      new Error("Missing environment or customer context for Data Vault write request.")
    );
  }

  const searchParams = new URLSearchParams({
    environment_id: String(resolvedEnvironmentId),
    customer_id: String(resolvedCustomerId)
  });

  const url = buildApiUrl(
    `/source-target-entity-rdv-column-mapping-update?${searchParams.toString()}`
  );
  return api.put<SaveColumnMappingsResponse>(url, payloads, getAccessToken(), {
    options: { isUrlExclusive: true }
  });
}

export function deleteSourceEntityColumnMappings(
  payloads: DeleteColumnMappingPayload[],
  environmentId?: string | number | null,
  customerId?: string | number | null
) {
  const resolvedEnvironmentId = environmentId ?? getEnvironmentId();
  const resolvedCustomerId = customerId ?? getCustomerId();

  if (!resolvedEnvironmentId || !resolvedCustomerId) {
    return Promise.reject(
      new Error("Missing environment or customer context for Data Vault delete request.")
    );
  }

  const searchParams = new URLSearchParams({
    environment_id: String(resolvedEnvironmentId),
    customer_id: String(resolvedCustomerId)
  });

  const url = buildApiUrl(
    `/source-target-entity-rdv-column-mapping-delete?${searchParams.toString()}`
  );
  return api.del<{ status?: string; message?: string }>(url, payloads, getAccessToken(), {
    options: { isUrlExclusive: true }
  });
}

export type AtomicColumnMappingSave = {
  create?: SaveColumnMappingPayload[];
  update?: SaveColumnMappingPayload[];
  delete?: DeleteColumnMappingPayload[];
};

/**
 * Save a whole column edit in one request.
 *
 * The three calls this replaces each ran their own transaction and their own validation,
 * so an edit that spanned two of them was judged halfway through. Moving one hub key while
 * adding another is exactly that shape: the new key lands on position 4 in the create call
 * while the key being moved off 4 is still sitting there, waiting for the update call that
 * has not run yet. The backend rejected it with "duplicate business_key_order values" for a
 * payload whose final state was perfectly valid.
 *
 * The three endpoints still exist and still work for single-kind edits.
 */
export function saveSourceEntityColumnMappings(
  payload: AtomicColumnMappingSave,
  environmentId?: string | number | null,
  customerId?: string | number | null
) {
  const resolvedEnvironmentId = environmentId ?? getEnvironmentId();
  const resolvedCustomerId = customerId ?? getCustomerId();

  if (!resolvedEnvironmentId || !resolvedCustomerId) {
    return Promise.reject(
      new Error("Missing environment or customer context for Data Vault write request.")
    );
  }

  const searchParams = new URLSearchParams({
    environment_id: String(resolvedEnvironmentId),
    customer_id: String(resolvedCustomerId)
  });

  const url = buildApiUrl(
    `/source-target-entity-rdv-column-mapping-save?${searchParams.toString()}`
  );
  return api.post<SaveColumnMappingsResponse>(
    url,
    {
      create: payload.create ?? [],
      update: payload.update ?? [],
      delete: payload.delete ?? []
    },
    getAccessToken(),
    { options: { isUrlExclusive: true } }
  );
}

export function updateDataVaultMappings(
  payloads: CreateMappingPayload[],
  environmentId?: string | number | null,
  customerId?: string | number | null
) {
  const resolvedEnvironmentId = environmentId ?? getEnvironmentId();
  const resolvedCustomerId = customerId ?? getCustomerId();

  if (!resolvedEnvironmentId || !resolvedCustomerId) {
    return Promise.reject(
      new Error("Missing environment or customer context for Data Vault write request.")
    );
  }

  const searchParams = new URLSearchParams({
    environment_id: String(resolvedEnvironmentId),
    customer_id: String(resolvedCustomerId)
  });

  const url = buildApiUrl(
    `/source-target-entity-rdv-mapping-update?${searchParams.toString()}`
  );

  return api.put<CreateMappingResponse>(url, payloads, getAccessToken(), {
    options: { isUrlExclusive: true }
  });
}

export function saveWorkspaceLayout(
  entries: WorkspaceLayoutEntry[],
  environmentId?: string | number | null,
  customerId?: string | number | null
) {
  if (!entries.length) {
    return Promise.resolve({ status: "skipped" });
  }
  const resolvedEnvironmentId = environmentId ?? getEnvironmentId();
  const resolvedCustomerId = customerId ?? getCustomerId();

  if (!resolvedEnvironmentId || !resolvedCustomerId) {
    return Promise.reject(
      new Error("Missing environment or customer context for layout sync.")
    );
  }

  const searchParams = new URLSearchParams({
    environment_id: String(resolvedEnvironmentId),
    customer_id: String(resolvedCustomerId)
  });

  const url = buildApiUrl(`/data-vault-layout-update?${searchParams.toString()}`);
  return api.put<{ status?: string }>(url, entries, getAccessToken(), {
    options: { isUrlExclusive: true }
  });
}

export function deleteTargetEntityRdvNode(
  targetEntityRdvId: number,
  environmentId?: string | number | null,
  customerId?: string | number | null
) {
  const resolvedEnvironmentId = environmentId ?? getEnvironmentId();
  const resolvedCustomerId = customerId ?? getCustomerId();
  if (!resolvedEnvironmentId || !resolvedCustomerId) {
    return Promise.reject(
      new Error("Missing environment or customer context for Data Vault delete request.")
    );
  }
  const searchParams = new URLSearchParams({
    target_entity_rdv_id: String(targetEntityRdvId),
    environment_id: String(resolvedEnvironmentId),
    customer_id: String(resolvedCustomerId)
  });
  const url = buildApiUrl(`/target-entity-rdv-delete?${searchParams.toString()}`);
  return api.del<DeleteTargetEntityResponse>(url, undefined, getAccessToken(), {
    options: { isUrlExclusive: true }
  });
}

export function deleteTargetEntityRdvLinkMember(
  linkMemberId: number,
  environmentId?: string | number | null,
  customerId?: string | number | null
) {
  const resolvedEnvironmentId = environmentId ?? getEnvironmentId();
  const resolvedCustomerId = customerId ?? getCustomerId();
  if (!resolvedEnvironmentId || !resolvedCustomerId) {
    return Promise.reject(
      new Error("Missing environment or customer context for Data Vault relationship delete request.")
    );
  }
  const searchParams = new URLSearchParams({
    environment_id: String(resolvedEnvironmentId),
    customer_id: String(resolvedCustomerId),
    link_member_id: String(linkMemberId)
  });
  const url = buildApiUrl(`/target-entity-rdv-link-member-hard?${searchParams.toString()}`);
  return api.del<{ deleted_link_member_id?: number; link_member_id?: number }>(
    url,
    undefined,
    getAccessToken(),
    {
      options: { isUrlExclusive: true }
    }
  );
}

export function deleteTargetEntityRdvSatMember(
  satMemberId: number,
  environmentId?: string | number | null,
  customerId?: string | number | null
) {
  const resolvedEnvironmentId = environmentId ?? getEnvironmentId();
  const resolvedCustomerId = customerId ?? getCustomerId();
  if (!resolvedEnvironmentId || !resolvedCustomerId) {
    return Promise.reject(
      new Error("Missing environment or customer context for Data Vault relationship delete request.")
    );
  }
  const searchParams = new URLSearchParams({
    environment_id: String(resolvedEnvironmentId),
    customer_id: String(resolvedCustomerId),
    sat_member_id: String(satMemberId)
  });
  const url = buildApiUrl(`/target-entity-rdv-sat-member-hard?${searchParams.toString()}`);
  return api.del<{ deleted_sat_member_id?: number; sat_member_id?: number }>(
    url,
    undefined,
    getAccessToken(),
    {
      options: { isUrlExclusive: true }
    }
  );
}

export type DataModelFileRecord = {
  filename: string;
  model_generation_id: string;
  subject_area: string;
  layer_type: string;
  is_current: boolean;
  environment_id: number;
  data_model_id: number;
  created_at: string;
};

export type GeneratorOwnedColumnsResponse = {
  generator_owned_columns: string[];
  stripped_for_constructs: string[];
};

/**
 * Columns the RDV templates emit themselves, served from the generator's own constant so
 * the blade cannot drift from it. Cached for the session — the list only changes when the
 * backend deploys.
 */
let generatorOwnedColumnsCache: Promise<Set<string>> | null = null;

export function fetchGeneratorOwnedColumns(): Promise<Set<string>> {
  if (!generatorOwnedColumnsCache) {
    generatorOwnedColumnsCache = api
      .get<GeneratorOwnedColumnsResponse>(
        buildApiUrl("/rdv-generator-owned-columns"),
        getAccessToken(),
        { options: { isUrlExclusive: true } }
      )
      .then(
        (response) =>
          new Set((response?.generator_owned_columns ?? []).map((name) => name.toLowerCase()))
      )
      .catch((error) => {
        // Never block modelling on this: the helper falls back to its bundled list.
        console.error("Unable to load generator-owned columns", error);
        generatorOwnedColumnsCache = null;
        return new Set<string>();
      });
  }
  return generatorOwnedColumnsCache;
}
